import _ from 'lodash';
import moment from 'moment';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';

// Centralized email validation regex - allows lowercase letters, numbers, periods, plus, hyphen, and underscore
export const EMAIL_REGEX = /^[a-z0-9._+-]+@[a-z0-9._-]+\.[a-z]{2,}$/;
export const EMAIL_VALIDATION_MESSAGE = 'Email address can only contain lowercase letters, numbers, periods, plus, hyphen, and underscore.';

export const UserRole = [
  {
    name: "Principle Investigator",
    value: "PI",
  },
  {
    name: "Site Admin",
    value: "site_admin",
  },
  {
    name: "Site Co-ordinator",
    value: "site_coordinator",
  },
];

export const extractFilenameFromKey = (key) => {
  if (key) {
    // Split the key by '/'
    const parts = key.split("/");
    // Get the last part (filename)
    const filename = parts[parts.length - 1];
    return filename;
  }
  return;
};
export const getSeriesColor = (index) => {
  const colorArr = ["#2E63DF", "#00928F", "#F765A3", "#A155B9", "#FF897D", "#F2994A", "#219653", "#2289C2", "#6C1CED", "#64CC26", "#EC7E2D"];
  if (index > colorArr.length - 1) {
    const clr = colorArr[(index - 1) % (colorArr.length - 1)];
    return clr;
  }
  return colorArr[index];
}
export const getLeftNavCategoryIcon = (category) => {
  switch (category) {
    case "AccVerification":
      return AccountBalanceIcon;
    case "MH":
      return MedicalServicesIcon;
  }
}

export const mergeCustomFields = (subjectEnrollConfiguration, customFields) => {
  const _tempConfiguration = JSON.parse(JSON.stringify(subjectEnrollConfiguration));
  _.each(customFields, (customField) => {
    const stepKey = customField.stepKey;
    const sectionKey = customField.sectionKey;
    const subSectionKey = customField.subSectionKey;
    const categoryKey = customField.categoryKey;

    const stepData = _.filter(_tempConfiguration?.steps, (step) => {
      return step.key == stepKey;
    })
    const sectionData = _.filter(stepData[0]?.sections, (section) => {
      return section.key == sectionKey;
    });
    let categoryData = [];
    if (subSectionKey) {
      const subSectionData = _.filter(sectionData[0]?.subTabs, (subSection) => {
        return subSection.key == subSectionKey;
      });
      categoryData = _.filter(subSectionData[0]?.categories, (category) => {
        return category.key == categoryKey;
      });
    } else {
      categoryData = _.filter(sectionData[0]?.categories, (category) => {
        return category.key == categoryKey;
      });
    }


    if (categoryData[0]) {
      let categoryObject = categoryData[0];
      categoryObject.fields = categoryObject.fields?.concat(customField?.customFields);
    }
  })
  return _tempConfiguration;
}

export function maskString(str) {
  // Replace each character with a masking character
  const str1 = str + '';
  return str1.replace(/./g, '*');
}


export function combineDateTime(dateString, timeString) {
  const date = moment(dateString);

  // Parse the time string using Moment.js
  const time = moment(timeString, 'HH:mm:ss');

  // Set the time component of the date to the parsed time
  date.set({
    hour: time.hours(),
    minute: time.minutes(),
    second: time.seconds(),
  });

  // Format the result as a UTC string
  const utcDateString = date.format('MM-DD-YYYYTHH:mm');
  return utcDateString;
}

export const parseIntOrZero = (val) => {
  const n = parseInt(val, 10);
  return isNaN(n) ? 0 : n;
};

/**
 * Extracts a user-friendly error message from API error responses
 * @param {Object} error - The error object from RTK Query mutation
 * @param {Object} options - Options for custom error messages
 * @param {string} options.defaultMessage - Default message if error cannot be extracted
 * @param {Object} options.customMessages - Map of error codes/keys to custom messages
 * @returns {string} User-friendly error message
 */
export const extractErrorMessage = (error, options = {}) => {
  const { defaultMessage = 'An unexpected error occurred. Please try again.', customMessages = {} } = options;

  if (!error) {
    return defaultMessage;
  }

  // Check for network errors
  if (error.status === 'FETCH_ERROR' || error.error === 'FETCH_ERROR') {
    return 'Network error. Please check your internet connection and try again.';
  }

  // Check for timeout errors
  if (error.status === 'TIMEOUT_ERROR' || error.error === 'TIMEOUT_ERROR') {
    return 'Request timed out. Please try again.';
  }

  // Check for custom messages first
  const errorKey = error?.data?.error || error?.data?.message || error?.error;
  if (errorKey && customMessages[errorKey]) {
    return customMessages[errorKey];
  }

  // Extract message from error.data.message
  if (error?.data?.message) {
    const message = error.data.message;
    // Handle specific error cases
    if (typeof message === 'string') {
      // Check for invalid_grant (invalid credentials)
      if (message.includes('invalid_grant') || message.toLowerCase().includes('invalid')) {
        return 'Invalid username or password. Please check your credentials and try again.';
      }
      // Check for account locked/disabled
      if (message.toLowerCase().includes('locked') || message.toLowerCase().includes('disabled')) {
        return 'Your account has been locked. Please contact support.';
      }
      // Check for account not found
      if (message.toLowerCase().includes('not found') || message.toLowerCase().includes('user not found')) {
        return 'User not found. Please check your email address.';
      }
      // Return the message as-is if it's user-friendly
      return message;
    }
  }

  // Extract message from error.data.error_description
  if (error?.data?.error_description) {
    return error.data.error_description;
  }

  // Extract message from error.data.error
  if (error?.data?.error) {
    const errorCode = error.data.error;
    if (errorCode === 'invalid_grant') {
      return 'Invalid username or password. Please check your credentials and try again.';
    }
    if (errorCode === 'invalid_client') {
      return 'Authentication failed. Please contact support.';
    }
  }

  // Handle HTTP status codes
  if (error?.status) {
    const status = error.status;
    if (status === 400) {
      return 'Invalid request. Please check your input and try again.';
    }
    if (status === 401) {
      return 'Invalid username or password. Please check your credentials and try again.';
    }
    if (status === 403) {
      return 'Access denied. Please contact support if you believe this is an error.';
    }
    if (status === 404) {
      return 'Resource not found. Please check your input and try again.';
    }
    if (status === 429) {
      return 'Too many requests. Please wait a moment and try again.';
    }
    if (status >= 500) {
      return 'Server error. Please try again later or contact support if the problem persists.';
    }
  }

  // Fallback to default message
  return defaultMessage;
};

/**
 * Returns a user-friendly, sanitized message from API errors (e.g. Stripe onboarding).
 */
export const sanitizeApiErrorMessage = (error, options = {}) => {
  const { defaultMessage = 'Unable to save bank details. Please try again.' } = options;

  if (!error) {
    return defaultMessage;
  }

  let message;

  if (error?.data?.message) {
    const rawMessage = error.data.message;
    if (Array.isArray(rawMessage)) {
      message = rawMessage.join(' ');
    } else if (typeof rawMessage === 'string') {
      message = rawMessage;
    }
  }

  if (!message && typeof error?.message === 'string' && !error?.status) {
    message = error.message;
  }

  if (!message) {
    message = extractErrorMessage(error, { defaultMessage });
  }

  message = message
    .replace(/^Stripe error:\s*/i, '')
    .replace(/\s*\((?:req|cus|acct)_[a-zA-Z0-9]+\)\.?/g, '')
    .replace(/\s*\(id:\s*[^)]+\)\.?/gi, '')
    .replace(/\s*;?\s*See https?:\/\/[^\s]+/gi, '')
    .trim();

  const stripeCustomerExistsMatch = message.match(
    /A Stripe Customer with email\s+(\S+)\s+already exists/i
  );
  if (stripeCustomerExistsMatch) {
    return `An account with email ${stripeCustomerExistsMatch[1]} is already registered. Please use a different email address or contact support.`;
  }

  const stripeConnectExistsMatch = message.match(
    /A Stripe Connect account with email\s+(\S+)\s+already exists/i
  );
  if (stripeConnectExistsMatch) {
    return `A payment account with email ${stripeConnectExistsMatch[1]} is already registered. Please use a different email address or contact support.`;
  }

  const entityExistsMatch = message.match(
    /An entity with email\s+(\S+)\s+already exists/i
  );
  if (entityExistsMatch) {
    return `An account with email ${entityExistsMatch[1]} already exists in the system. Please use a different email address or contact support.`;
  }

  if (/unsupported state|insular areas|freely associated states/i.test(message)) {
    return 'The address state is not supported. Please use a US state address (territories and insular areas are not supported).';
  }

  if (/already exists/i.test(message)) {
    const emailMatch = message.match(/email\s+(\S+)/i);
    if (emailMatch) {
      return `An account with email ${emailMatch[1]} already exists. Please use a different email address or contact support.`;
    }
    return message;
  }

  return message || defaultMessage;
};

/**
 * Extracts a NestJS / RTK Query error message for query-management and similar APIs.
 * Joins ValidationPipe string[] messages and maps leftover HTTP statuses.
 * Does not remap generic "not found" text (unlike extractErrorMessage).
 */
export const getNestApiErrorMessage = (error, fallback = 'Something went wrong. Please try again.') => {
  if (!error) {
    return fallback;
  }

  const status = error.status ?? error?.data?.statusCode;

  if (status === 'FETCH_ERROR' || error.error === 'FETCH_ERROR') {
    return 'Network error. Please check your internet connection and try again.';
  }
  if (status === 'TIMEOUT_ERROR' || error.error === 'TIMEOUT_ERROR') {
    return 'Request timed out. Please try again.';
  }

  const rawMessage = error?.data?.message ?? (error?.message !== 'Rejected' ? error?.message : null);
  if (Array.isArray(rawMessage)) {
    const joined = rawMessage.filter(Boolean).join('. ');
    if (joined) {
      return joined;
    }
  } else if (typeof rawMessage === 'string' && rawMessage.trim()) {
    return rawMessage;
  }

  if (status === 403) {
    return 'You do not have permission to perform this action.';
  }
  if (status === 404) {
    return 'The requested resource was not found.';
  }
  if (typeof status === 'number' && status >= 500) {
    return 'Server error. Please try again later.';
  }

  return fallback;
};
